import { AppShell, Text, Group, Avatar, Box, ScrollArea, Badge, Burger, Tooltip, Popover, Stack, ActionIcon, Divider, Center, Loader, Modal, SimpleGrid, UnstyledButton } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import {
  IconLayoutDashboard, IconDeviceDesktop, IconTool, IconAlertTriangle,
  IconClipboardList, IconLogout, IconArrowsExchange, IconTransfer,
  IconFileText, IconUsers, IconSettings, IconActivity,
  IconBell, IconPackage, IconClipboardCheck, IconKey, IconListCheck,
  IconScan, IconCheckbox, IconCheck, IconBuilding, IconTicket, IconBottle, IconHome2, IconShield,
} from '@tabler/icons-react'
import { useAuth } from '../../hooks/useAuth'
import { Capacitor } from '@capacitor/core'
import { getNotifications, markNotificationRead, markAllNotificationsRead, getPendingBorrows, getHandovers, getTransfers } from '../../api/client'

export interface NavItem {
  path: string
  labelKey: string
  icon: any
  always?: boolean
  perm?: string
  roles?: string[]
  superAdminOnly?: boolean
  feature?: string   // tenant-level feature toggle (SuperAdmin controls)
  access?: string    // user-level access control (Access Level controls, ADMIN bypasses)
  accessAny?: string[] // แสดงถ้ามี key ใดใน array อยู่ใน allowedPages (ใช้กับ Reports)
}

export const NAV_GROUPS: { label: string; labelKey: string; items: NavItem[] }[] = [
  {
    label: 'Main',
    labelKey: 'nav.main',
    items: [
      { path: '/',                labelKey: 'nav.home',          icon: IconHome2,           always: true },
      { path: '/dashboard',       labelKey: 'nav.dashboard',     icon: IconLayoutDashboard, always: true, access: 'module:dashboard-asset' },
      { path: '/assets',         labelKey: 'nav.assets',        icon: IconDeviceDesktop,   always: true, feature: 'module:assets' },
      { path: '/scan',           labelKey: 'nav.scan',          icon: IconScan,            always: true, feature: 'module:assets' },
      { path: '/approvals',      labelKey: 'nav.approvals',     icon: IconCheckbox,        perm: 'APPROVE_BORROW' },
      { path: '/borrow',         labelKey: 'nav.borrow',        icon: IconArrowsExchange,  always: true,  feature: 'module:borrows' },
      { path: '/transfer',       labelKey: 'nav.transfer',      icon: IconTransfer,        always: true,  feature: 'module:transfers' },
      { path: '/handover',       labelKey: 'nav.handover',      icon: IconFileText,        always: true,  feature: 'module:handover' },
      { path: '/asset-checking', labelKey: 'nav.assetChecking', icon: IconListCheck,       roles: ['ADMIN','ASSET_MANAGER'], feature: 'module:audit' },
      { path: '/maintenance',    labelKey: 'nav.maintenance',   icon: IconTool,            always: true,  feature: 'module:maintenance' },
      { path: '/licenses',        labelKey: 'nav.licenses',       icon: IconKey,             always: true,  feature: 'module:licenses' },
      { path: '/write-off',      labelKey: 'nav.writeoff',      icon: IconAlertTriangle,   roles: ['ADMIN','ASSET_MANAGER'], feature: 'module:depreciation' },
    ],
  },
  {
    label: 'Facility',
    labelKey: 'nav.facilityGroup',
    items: [
      { path: '/facility',     labelKey: 'nav.facilityDashboard', icon: IconLayoutDashboard, always: true, feature: 'module:work-orders' },
      { path: '/work-orders',  labelKey: 'nav.workOrders',  icon: IconTicket,          always: true, feature: 'module:work-orders' },
      { path: '/hk-orders',    labelKey: 'nav.hkOrders',    icon: IconBottle,          always: true, feature: 'module:hk-orders' },
    ],
  },
  {
    label: 'Operations',
    labelKey: 'nav.operations',
    items: [
      { path: '/stock-dashboard', labelKey: 'nav.stockDashboard', icon: IconLayoutDashboard, always: true, feature: 'module:inventory' },
      { path: '/stock-reports',   labelKey: 'nav.stockReports',   icon: IconClipboardList,   always: true, feature: 'module:inventory' },
      { path: '/inventory',       labelKey: 'nav.inventory',      icon: IconPackage,         always: true, feature: 'module:inventory' },
      { path: '/stock-confirm',   labelKey: 'nav.stockConfirm',   icon: IconClipboardCheck,  roles: ['ADMIN', 'ASSET_MANAGER'], feature: 'module:inventory' },
      { path: '/reports',         labelKey: 'nav.reports',        icon: IconClipboardList,   perm: 'VIEW_REPORTS', accessAny: ['report:asset-summary','report:depreciation','report:maintenance','report:borrow','report:audit-log'] },
    ],
  },
  {
    label: 'Admin',
    labelKey: 'nav.admin',
    items: [
      { path: '/users',           labelKey: 'nav.users',          icon: IconUsers,          perm: 'MANAGE_USERS', access: 'admin:users' },
      { path: '/changelog',       labelKey: 'nav.changelog',      icon: IconClipboardCheck, roles: ['ADMIN','ASSET_MANAGER'], access: 'module:changelog' },
      { path: '/configuration',   labelKey: 'nav.configuration',  icon: IconSettings,       always: true, access: 'admin:configuration' },
      { path: '/audit-log',       labelKey: 'nav.auditLog',       icon: IconActivity,       perm: 'VIEW_AUDIT_LOG', access: 'report:audit-log' },
      { path: '/company-profile', labelKey: 'nav.companyProfile', icon: IconBuilding,       roles: ['ADMIN'] },
      { path: '/access-levels',   labelKey: 'nav.accessLevels',   icon: IconShield,         roles: ['ADMIN'] },
    ],
  },
  {
    label: 'Platform',
    labelKey: 'nav.platform',
    items: [
      // ('/super-admin' nav item ตัดออกแล้ว — ดู App.tsx)
    ],
  },
]

export interface NavVisibilityContext {
  isSuperAdminRole: boolean
  isAdminRole: boolean
  roles: string[]
  featuresLoaded: boolean
  hasPermission: (perm: string) => boolean
  hasFeature: (feature: string) => boolean
  hasPageAccess: (key: string) => boolean
}

/** เกณฑ์เดียวกันเป๊ะกับที่ sidebar ใช้กรอง — HomePage ก็ใช้ตัวนี้ กันไม่ให้ logic เพี้ยนจากกัน */
export function isNavItemVisible(i: NavItem, ctx: NavVisibilityContext): boolean {
  const { isSuperAdminRole, isAdminRole, roles, featuresLoaded, hasPermission, hasFeature, hasPageAccess } = ctx
  return (
    (isSuperAdminRole || i.always || !i.perm || hasPermission(i.perm)) &&
    (isSuperAdminRole || !i.roles || i.roles.some((r: string) => roles.includes(r))) &&
    (!i.feature || !featuresLoaded || hasFeature(i.feature)) &&
    (isSuperAdminRole || !i.feature || hasPageAccess(i.feature)) &&
    (isSuperAdminRole || isAdminRole || !i.access || hasPageAccess(i.access)) &&
    (isSuperAdminRole || isAdminRole || !i.accessAny || i.accessAny.some(k => hasPageAccess(k))) &&
    (!i.superAdminOnly || isSuperAdminRole)
  )
}

// แสดงโลโก้ของบริษัทที่ login อยู่ ถ้าตั้งไว้ ไม่งั้น fallback เป็นโลโก้ AssetOS เริ่มต้น
export function BrandMark({ logoUrl, name }: { logoUrl?: string | null; name?: string | null }) {
  if (logoUrl) {
    return (
      <Group gap={10} align="center" wrap="nowrap">
        <Box style={{
          width: 28, height: 28, borderRadius: 7, background: '#fff', overflow: 'hidden',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <img src={logoUrl} alt={name ?? 'Logo'} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </Box>
        <Text style={{
          fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 600, color: '#f8fafc',
          letterSpacing: '-0.2px', lineHeight: 1.2, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {name}
        </Text>
      </Group>
    )
  }
  return (
    <Group gap={10} align="center">
      <Box style={{ width: 28, height: 28, borderRadius: 7, background: 'linear-gradient(135deg, #14b8a6, #6ee7b7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <IconDeviceDesktop size={15} color="#0f172a" stroke={2} />
      </Box>
      <Box>
        <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: 15, fontWeight: 600, color: '#f8fafc', letterSpacing: '-0.3px', lineHeight: 1 }}>
          Asset<span style={{ color: '#6ee7b7' }}>OS</span>
        </Text>
        <Text style={{ fontSize: 10, color: '#475569', marginTop: 2, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
          Management Platform
        </Text>
      </Box>
    </Group>
  )
}

export default function Layout() {
  const { t, i18n } = useTranslation()
  const [opened, { toggle, close }] = useDisclosure()
  const { username, fullName, roles, isSuperAdmin, companyLogoUrl, companyName, doLogout, hasPermission, hasFeature, featuresLoaded, hasPageAccess } = useAuth()
  const isSuperAdminRole = isSuperAdmin || roles.includes('SUPER_ADMIN')
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useMediaQuery('(max-width: 768px)')
  const isTablet = useMediaQuery('(max-width: 1024px)')
  const [bellOpen, { toggle: toggleBell, close: closeBell }] = useDisclosure(false)
  const [notifs, setNotifs] = useState<any[]>([])
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0)
  const [pendingHandoverApprovalCount, setPendingHandoverApprovalCount] = useState(0)
  const [pendingHandoverCount, setPendingHandoverCount] = useState(0)
  const pendingCount = pendingApprovalsCount + pendingHandoverApprovalCount + pendingHandoverCount

  const loadNotifs = async () => {
    try { const res = await getNotifications(); setNotifs(res.data) } catch {}
  }

  // โหลด pending approvals (borrow + transfer + handover ที่รออนุมัติ) กับ pending handover ที่รอผู้รับยืนยัน
  // (หลังอนุมัติแล้ว) แยกกัน — handover ตอนนี้มีขั้นตอนอนุมัติแล้ว (เพิ่มทีหลัง) แต่หลังอนุมัติแล้วยังมี
  // ขั้นที่ผู้จัดการทำอะไรเองไม่ได้อีก (แค่รอผู้รับกดยืนยัน อาจผ่าน magic link ไม่ต้อง Login เลยด้วยซ้ำ)
  // เลยยังแยก 2 กลุ่มไว้เหมือนเดิม แค่ handover ตอนนี้มีของอยู่ในทั้ง 2 กลุ่มได้ตามช่วงสถานะ
  // transfer พับรวมเข้า pendingApprovalsCount ได้เลย (ไม่ต้องแยก badge แบบ handover) เพราะหน้า
  // /approvals ที่ badge นี้ลิงก์ไปหา รองรับทั้ง borrow และ transfer อยู่แล้ว — เดิม transfer ไม่เคย
  // ถูกดึงมานับในกระดิ่งเลยสักครั้ง เจอตอนคุยกับ user เรื่องช่องทางแจ้งเตือนที่ควรจะมีให้ครบ
  const loadPendingCount = async () => {
    if (!hasPermission('APPROVE_BORROW')) { setPendingApprovalsCount(0); setPendingHandoverApprovalCount(0); setPendingHandoverCount(0); return }
    try {
      const [borrows, transfers, handovers] = await Promise.all([
        getPendingBorrows(),
        getTransfers(),
        getHandovers(),
      ])
      const pendingBorrows = (borrows.data ?? []).filter((b: any) => b.status === 'PENDING').length
      const pendingTransfers = (transfers.data ?? []).filter((tr: any) => tr.status === 'PENDING').length
      setPendingApprovalsCount(pendingBorrows + pendingTransfers)
      setPendingHandoverApprovalCount((handovers.data ?? []).filter((h: any) => h.status === 'PENDING_APPROVAL').length)
      setPendingHandoverCount((handovers.data ?? []).filter((h: any) => h.status === 'DRAFT').length)
    } catch { setPendingApprovalsCount(0); setPendingHandoverApprovalCount(0); setPendingHandoverCount(0) }
  }

  useEffect(() => {
    loadNotifs()
    loadPendingCount()
    const t = setInterval(() => { loadNotifs(); loadPendingCount() }, 60_000)
    return () => clearInterval(t)
  }, [])

  const handleMarkRead = async (id: number) => {
    await markNotificationRead(id).catch(() => {})
    setNotifs(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n))
  }

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead().catch(() => {})
    setNotifs(prev => prev.map(n => ({ ...n, isRead: true })))
  }

  const unreadCount = notifs.filter(n => !n.isRead).length + pendingCount

  const goTo = (path: string) => { navigate(path); close() }

  const changeLang = (lang: string) => {
    i18n.changeLanguage(lang)
    localStorage.setItem('lang', lang)
  }

  const allItems = NAV_GROUPS.flatMap(g => g.items)
  const isAdminRole = roles.includes('ADMIN') || roles.includes('SUPER_ADMIN')
  const visibleItems = allItems.filter(i =>
    (isSuperAdminRole || i.always || !i.perm || hasPermission(i.perm)) &&
    (isSuperAdminRole || !i.roles || i.roles.some((r: string) => roles.includes(r))) &&
    (!i.feature || !featuresLoaded || hasFeature(i.feature)) &&
    (isSuperAdminRole || !i.feature || hasPageAccess(i.feature)) &&
    (isSuperAdminRole || isAdminRole || !i.access || hasPageAccess(i.access)) &&
    (isSuperAdminRole || isAdminRole || !i.accessAny || i.accessAny.some(k => hasPageAccess(k))) &&
    (!i.superAdminOnly || isSuperAdminRole)
  )
  // ── บน native app (Capacitor) ให้ Quick Scan + Approvals ติด bottom nav เสมอ ──
  // (ถ้าปล่อยตามลำดับเดิม อาจหลุด top-4 ไปสำหรับ user ทั่วไป เพราะ Home/Dashboard/Assets
  // มาก่อนใน NAV_GROUPS — บน native ให้ความสำคัญกับ 2 ตัวนี้เป็นพิเศษตามที่ตกลงกันไว้
  // แต่ยังคงลำดับเดิมของรายการที่เหลือไว้ ไม่สลับทั้งหมด)
  const isNativeApp = Capacitor.isNativePlatform()
  const MOBILE_PRIORITY_PATHS = ['/scan', '/approvals']
  const bottomNav = isNativeApp
    ? (() => {
        const priority = visibleItems.filter(i => MOBILE_PRIORITY_PATHS.includes(i.path))
        const rest = visibleItems.filter(i => !MOBILE_PRIORITY_PATHS.includes(i.path))
        return [...priority, ...rest].slice(0, 4).sort(
          (a, b) => visibleItems.indexOf(a) - visibleItems.indexOf(b)
        )
      })()
    : visibleItems.slice(0, 4)
  const [menuOpen, { open: openMenu, close: closeMenu }] = useDisclosure(false)

  return (
    <>
      <AppShell
        navbar={{ width: 232, breakpoint: 'md', collapsed: { mobile: !opened } }}
        padding={0}
        styles={{
          main: { background: '#f1f5f9', minHeight: '100dvh', paddingBottom: (isMobile || isTablet) ? 'calc(64px + env(safe-area-inset-bottom))' : 0 },
          navbar: { background: '#0f172a', borderRight: 'none', display: 'flex', flexDirection: 'column', boxShadow: '1px 0 0 0 rgba(255,255,255,0.05)' },
        }}
      >
        <AppShell.Navbar>
          {/* Logo */}
          <Box style={{ padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
            <BrandMark logoUrl={companyLogoUrl} name={companyName} />
          </Box>

          {/* Nav groups */}
          <ScrollArea flex={1} py="xs" px={12}>
            {NAV_GROUPS.map((group) => {
              const groupItems = group.items.filter(i =>
              (isSuperAdminRole || i.always || !i.perm || hasPermission(i.perm)) &&
              (isSuperAdminRole || !i.roles || i.roles.some((r: string) => roles.includes(r))) &&
              (!i.feature || !featuresLoaded || hasFeature(i.feature)) &&
              (isSuperAdminRole || !i.feature || hasPageAccess(i.feature)) &&
              (isSuperAdminRole || isAdminRole || !i.access || hasPageAccess(i.access)) &&
    (isSuperAdminRole || isAdminRole || !i.accessAny || i.accessAny.some(k => hasPageAccess(k))) &&
              (!i.superAdminOnly || isSuperAdminRole)
            )
              if (groupItems.length === 0) return null
              return (
                <Box key={group.label} mb={8}>
                  <Text style={{ fontSize: 10, color: '#334155', letterSpacing: '0.8px', textTransform: 'uppercase', fontWeight: 600, padding: '8px 8px 4px' }}>
                    {group.label}
                  </Text>
                  {groupItems.map((item) => {
                    const active = location.pathname === item.path
                    const label = t(item.labelKey)
                    return (
                      <Box
                        key={item.path}
                        onClick={() => goTo(item.path)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '7px 10px', borderRadius: 7, cursor: 'pointer',
                          marginBottom: 1, transition: 'all 0.12s',
                          background: active ? 'rgba(110,231,183,0.10)' : 'transparent',
                        }}
                        onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.04)' }}
                        onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent' }}
                      >
                        <Box style={{ width: 16, flexShrink: 0 }}>
                          <item.icon size={15} color={active ? '#6ee7b7' : '#64748b'} stroke={active ? 2 : 1.5} />
                        </Box>
                        <Text style={{ fontSize: 13, fontWeight: active ? 500 : 400, color: active ? '#f1f5f9' : '#94a3b8', flex: 1 }}>
                          {label}
                        </Text>
                        {active && (
                          <Box style={{ width: 5, height: 5, borderRadius: '50%', background: '#6ee7b7', flexShrink: 0 }} />
                        )}
                      </Box>
                    )
                  })}
                </Box>
              )
            })}
          </ScrollArea>

          {/* Language + user footer */}
          <Box style={{ padding: '8px 12px 12px', borderTop: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
            {/* Language toggle */}
            <Group gap={4} mb={10} justify="center">
              {(['en', 'th'] as const).map(lang => (
                <Box
                  key={lang}
                  onClick={() => changeLang(lang)}
                  style={{
                    padding: '3px 14px', borderRadius: 5, cursor: 'pointer',
                    fontSize: 11, fontWeight: 500, transition: 'all 0.12s',
                    background: i18n.language === lang ? 'rgba(110,231,183,0.15)' : 'transparent',
                    color: i18n.language === lang ? '#6ee7b7' : '#475569',
                    border: i18n.language === lang ? '1px solid rgba(110,231,183,0.3)' : '1px solid transparent',
                  }}
                >
                  {lang.toUpperCase()}
                </Box>
              ))}
            </Group>

            {/* User */}
            <Group justify="space-between" wrap="nowrap">
              <Group gap={10} wrap="nowrap" style={{ cursor: 'pointer', flex: 1, minWidth: 0 }} onClick={() => goTo('/profile')}>
                <Avatar size={32} radius={8} color="teal" variant="filled" style={{ flexShrink: 0 }}>
                  {(fullName || username)?.[0]?.toUpperCase()}
                </Avatar>
                <Box style={{ minWidth: 0, flex: 1 }}>
                  <Text style={{ fontSize: 12, fontWeight: 500, color: '#f1f5f9', lineHeight: 1.3 }} truncate>
                    {fullName || username}
                  </Text>
                  <Text style={{ fontSize: 10, color: '#475569', marginTop: 1 }}>
                    {roles[0] ?? 'USER'}
                  </Text>
                </Box>
              </Group>
              <Tooltip label="Sign out" position="right">
                <Box
                  onClick={() => { doLogout(); navigate('/login') }}
                  style={{ cursor: 'pointer', padding: 6, borderRadius: 6, display: 'flex', transition: 'background 0.12s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <IconLogout size={15} color="#475569" />
                </Box>
              </Tooltip>
            </Group>
          </Box>
        </AppShell.Navbar>

        <AppShell.Main>
          {/* Top bar — desktop only */}
          {!isMobile && !isTablet && (
            <Box style={{ height: 52, background: '#ffffff', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', paddingInline: 24, gap: 12, position: 'sticky', top: 0, zIndex: 100 }}>
              <Text style={{ fontSize: 12, color: '#94a3b8', fontWeight: 400 }}>
                AssetOS
              </Text>
              <Text style={{ fontSize: 12, color: '#cbd5e1' }}>›</Text>
              <Text style={{ fontSize: 12, color: '#0f172a', fontWeight: 500 }}>
                {t(allItems.find(i => i.path === location.pathname)?.labelKey ?? 'nav.dashboard')}
              </Text>

              <Box style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Popover opened={bellOpen} onClose={closeBell} width={320} position="bottom-end" shadow="lg" radius="lg" withArrow>
                  <Popover.Target>
                    <Tooltip label="Notifications" disabled={bellOpen}>
                      <Box onClick={toggleBell} style={{ position: 'relative', width: 32, height: 32, borderRadius: 8, background: unreadCount > 0 ? '#fef3c7' : '#f8fafc', border: `1px solid ${unreadCount > 0 ? '#fbbf24' : '#e2e8f0'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                        <IconBell size={15} color={unreadCount > 0 ? '#d97706' : '#64748b'} />
                        {unreadCount > 0 && (
                          <Box style={{ position: 'absolute', top: -4, right: -4, background: '#ef4444', borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Text size="xs" c="white" fw={700} style={{ fontSize: 9, lineHeight: 1 }}>{unreadCount > 9 ? '9+' : unreadCount}</Text>
                          </Box>
                        )}
                      </Box>
                    </Tooltip>
                  </Popover.Target>
                  <Popover.Dropdown p={0} style={{ overflow: 'hidden' }}>
                    {/* Header */}
                    <Group justify="space-between" px={14} py={10} style={{ borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                      <Text size="sm" fw={700} style={{ fontFamily: 'Inter,sans-serif' }}>
                        Notifications {unreadCount > 0 && <Badge color="red" size="xs" circle ml={4}>{unreadCount}</Badge>}
                      </Text>
                      {unreadCount > 0 && (
                        <Text size="xs" c="teal" style={{ cursor: 'pointer', fontWeight: 500 }} onClick={handleMarkAllRead}>
                          Mark all read
                        </Text>
                      )}
                    </Group>
                    {/* Pending Approvals / Deliveries Section — แสดงเฉพาะ ASSET_MANAGER/ADMIN */}
                    {pendingCount > 0 && (
                      <Box style={{ background: '#fffbeb', borderBottom: '1px solid #fef3c7' }}>
                        <Box px={14} py={8}>
                          <Text size="xs" fw={700} c="orange" style={{ textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                            ⏳ {t('nav.pendingTasks')}
                          </Text>
                          <Group gap={6} wrap="wrap">
                            {pendingApprovalsCount > 0 && (
                              <Badge size="sm" color="orange" variant="light"
                                style={{ cursor: 'pointer' }} onClick={() => { closeBell(); navigate('/approvals') }}>
                                {t('nav.approvalsPending', 'Approvals')} — {pendingApprovalsCount} {t('nav.pendingCount')}
                              </Badge>
                            )}
                            {pendingHandoverApprovalCount > 0 && (
                              <Badge size="sm" color="orange" variant="light"
                                style={{ cursor: 'pointer' }} onClick={() => { closeBell(); navigate('/handover') }}>
                                {t('handover.pendingApproval', 'Pending Approval')} — {pendingHandoverApprovalCount} {t('nav.pendingCount')}
                              </Badge>
                            )}
                            {pendingHandoverCount > 0 && (
                              <Badge size="sm" color="grape" variant="light"
                                style={{ cursor: 'pointer' }} onClick={() => { closeBell(); navigate('/handover') }}>
                                {t('nav.handoverAwaitingRecipient', 'Awaiting Recipient')} — {pendingHandoverCount} {t('nav.pendingCount')}
                              </Badge>
                            )}
                          </Group>
                        </Box>
                      </Box>
                    )}
                    {/* List */}
                    <Box style={{ maxHeight: 340, overflowY: 'auto' }}>
                      {notifs.length === 0 && pendingCount === 0 ? (
                        <Center p="lg">
                          <Stack align="center" gap={4}>
                            <IconBell size={28} color="#cbd5e1" />
                            <Text size="xs" c="dimmed">{t('nav.noNotifications')}</Text>
                          </Stack>
                        </Center>
                      ) : notifs.length === 0 ? null : notifs.map(n => (
                        <Box key={n.id}
                          onClick={() => handleMarkRead(n.id)}
                          style={{ padding: '10px 14px', borderBottom: '1px solid #f8fafc', background: n.isRead ? '#fff' : '#f0fdf4', cursor: 'pointer', transition: 'background 0.15s' }}>
                          <Group gap={8} wrap="nowrap" align="flex-start">
                            <Box style={{ width: 6, height: 6, borderRadius: '50%', background: n.isRead ? 'transparent' : '#22c55e', flexShrink: 0, marginTop: 5 }} />
                            <Box style={{ flex: 1, minWidth: 0 }}>
                              <Text size="xs" fw={n.isRead ? 400 : 600} lineClamp={2} style={{ color: '#374151' }}>{n.message}</Text>
                              <Text size="xs" c="dimmed" mt={2}>
                                {n.createdAt ? new Date(n.createdAt).toLocaleDateString('th-TH', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                              </Text>
                            </Box>
                            {!n.isRead && <IconCheck size={12} color="#22c55e" style={{ flexShrink: 0, marginTop: 4 }} />}
                          </Group>
                        </Box>
                      ))}
                    </Box>
                  </Popover.Dropdown>
                </Popover>
                <Avatar size={32} radius={8} color="teal" variant="filled" style={{ cursor: 'pointer' }} onClick={() => goTo('/profile')}>
                  {(fullName || username)?.[0]?.toUpperCase()}
                </Avatar>
              </Box>
            </Box>
          )}

          {/* Tablet top bar with hamburger */}
          {isTablet && !isMobile && (
            <Box style={{ height: 52, background: '#ffffff', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', paddingInline: 16, gap: 12, position: 'sticky', top: 0, zIndex: 100 }}>
              <Burger opened={opened} onClick={toggle} size="sm" color="#0f172a" />
              <Text style={{ fontSize: 13, color: '#0f172a', fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
                Asset<span style={{ color: '#14b8a6' }}>OS</span>
              </Text>
              <Box style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Box onClick={toggleBell} style={{ position: 'relative', width: 32, height: 32, borderRadius: 8, background: unreadCount > 0 ? '#fef3c7' : '#f8fafc', border: `1px solid ${unreadCount > 0 ? '#fbbf24' : '#e2e8f0'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  <IconBell size={15} color={unreadCount > 0 ? '#d97706' : '#64748b'} />
                  {unreadCount > 0 && (
                    <Box style={{ position: 'absolute', top: -3, right: -3, width: 14, height: 14, borderRadius: '50%', background: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Text style={{ fontSize: 8, color: '#fff', fontWeight: 700 }}>{unreadCount > 9 ? '9+' : unreadCount}</Text>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          <Outlet />
        </AppShell.Main>
      </AppShell>

      {/* ── Mobile Bottom Nav ── */}
      <Box
        hiddenFrom="md"
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          height: 64, background: '#0f172a',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'stretch', zIndex: 200,
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {bottomNav.map((item) => {
          const active = location.pathname === item.path
          return (
            <Box key={item.path} onClick={() => navigate(item.path)} style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 3, cursor: 'pointer',
              background: active ? 'rgba(110,231,183,0.08)' : 'transparent',
              borderTop: `2px solid ${active ? '#6ee7b7' : 'transparent'}`,
              transition: 'all 0.15s',
            }}>
              <item.icon size={20} color={active ? '#6ee7b7' : '#475569'} stroke={active ? 2 : 1.5} />
              <Text style={{ fontSize: 10, color: active ? '#6ee7b7' : '#475569', fontWeight: active ? 600 : 400, lineHeight: 1 }}>
                {t(item.labelKey)}
              </Text>
            </Box>
          )
        })}
        {/* More button — เปิด grid modal */}
        <Box onClick={openMenu} style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 3, cursor: 'pointer',
          background: menuOpen ? 'rgba(110,231,183,0.08)' : 'transparent',
          borderTop: `2px solid ${menuOpen ? '#6ee7b7' : 'transparent'}`,
          transition: 'all 0.15s',
        }}>
          <Burger opened={menuOpen} size={14} color={menuOpen ? '#6ee7b7' : '#475569'} lineSize={1.5} />
          <Text style={{ fontSize: 10, color: menuOpen ? '#6ee7b7' : '#475569', fontWeight: menuOpen ? 600 : 400, lineHeight: 1 }}>
            {t('nav.menu')}
          </Text>
        </Box>
      </Box>

      {/* ── Mobile Full-screen Menu Modal ── */}
      <Modal
        opened={menuOpen}
        onClose={closeMenu}
        fullScreen
        withCloseButton={false}
        padding={0}
        styles={{
          content: { background: '#0f172a' },
          body: { padding: 0, height: '100%' },
        }}
      >
        {/* Modal header */}
        <Box style={{
          padding: '16px 20px 12px',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Group gap={10}>
            <BrandMark logoUrl={companyLogoUrl} name={companyName} />
          </Group>
          <ActionIcon variant="subtle" onClick={closeMenu} size="lg">
            <Text style={{ fontSize: 20, color: '#64748b', lineHeight: 1 }}>✕</Text>
          </ActionIcon>
        </Box>

        {/* Language toggle — top of modal */}
        <Box style={{ padding: '8px 16px 0' }}>
          <Group gap={4} justify="flex-end">
            {(['en', 'th'] as const).map(lang => (
              <Box key={lang} onClick={() => changeLang(lang)} style={{
                padding: '3px 14px', borderRadius: 6, cursor: 'pointer',
                fontSize: 12, fontWeight: 600, transition: 'all 0.12s',
                background: i18n.language === lang ? 'rgba(110,231,183,0.15)' : 'rgba(255,255,255,0.04)',
                color: i18n.language === lang ? '#6ee7b7' : '#475569',
                border: i18n.language === lang ? '1px solid rgba(110,231,183,0.3)' : '1px solid rgba(255,255,255,0.08)',
              }}>
                {lang === 'th' ? '🇹🇭 TH' : '🇬🇧 EN'}
              </Box>
            ))}
          </Group>
        </Box>

        <ScrollArea h="calc(100vh - 130px)" px={16} py={12}>
          {NAV_GROUPS.map((group) => {
            const groupItems = group.items.filter(i =>
              (isSuperAdminRole || i.always || !i.perm || hasPermission(i.perm)) &&
              (isSuperAdminRole || !i.roles || i.roles.some((r: string) => roles.includes(r))) &&
              (!i.superAdminOnly || isSuperAdminRole)
            )
            if (groupItems.length === 0) return null
            return (
              <Box key={group.label} mb={20}>
                <Text style={{ fontSize: 10, color: '#334155', letterSpacing: '0.8px', textTransform: 'uppercase', fontWeight: 600, marginBottom: 8, paddingLeft: 4 }}>
                  {group.label}
                </Text>
                <SimpleGrid cols={3} spacing={8}>
                  {groupItems.map((item) => {
                    const active = location.pathname === item.path
                    return (
                      <UnstyledButton
                        key={item.path}
                        onClick={() => { navigate(item.path); closeMenu() }}
                        style={{
                          display: 'flex', flexDirection: 'column',
                          alignItems: 'center', justifyContent: 'center',
                          gap: 8, padding: '14px 8px',
                          borderRadius: 12, cursor: 'pointer',
                          background: active ? 'rgba(110,231,183,0.12)' : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${active ? 'rgba(110,231,183,0.3)' : 'rgba(255,255,255,0.06)'}`,
                          transition: 'all 0.12s',
                        }}
                      >
                        <Box style={{
                          width: 40, height: 40, borderRadius: 10,
                          background: active ? 'rgba(110,231,183,0.18)' : 'rgba(255,255,255,0.06)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <item.icon size={20} color={active ? '#6ee7b7' : '#94a3b8'} stroke={active ? 2 : 1.5} />
                        </Box>
                        <Text style={{ fontSize: 11, color: active ? '#6ee7b7' : '#94a3b8', fontWeight: active ? 600 : 400, lineHeight: 1.2, textAlign: 'center' }}>
                          {t(item.labelKey)}
                        </Text>
                      </UnstyledButton>
                    )
                  })}
                </SimpleGrid>
              </Box>
            )
          })}
        </ScrollArea>

        {/* User footer in modal */}
        <Box style={{ padding: '12px 20px 24px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <Group justify="space-between">
            <Group gap={10} style={{ cursor: 'pointer' }} onClick={() => { navigate('/profile'); closeMenu() }}>
              <Avatar size={36} radius={9} color="teal" variant="filled">
                {(fullName || username)?.[0]?.toUpperCase()}
              </Avatar>
              <Box>
                <Text style={{ fontSize: 13, fontWeight: 500, color: '#f1f5f9' }}>{fullName || username}</Text>
                <Text style={{ fontSize: 11, color: '#475569' }}>{roles[0] ?? 'USER'}</Text>
              </Box>
            </Group>
            <ActionIcon variant="subtle" onClick={() => { doLogout(); navigate('/login') }} size="lg">
              <IconLogout size={18} color="#475569" />
            </ActionIcon>
          </Group>
        </Box>
      </Modal>
    </>
  )
}
